class ColorHelper {
  /**
   * This method will convert the given color to its red, green and blue values (0-255)
   * @param {string} color for example: "rgba(0, 128, 0, 1)", "rgb(0, 128, 0)", "#008000" or "#080"
   * @returns {{ r: number, g: number, b: number }|null} null when the color could not be read
   */
  static toRgb(color) {
    if (typeof color !== "string") {
      return null;
    }

    //rgb() and rgba() colors
    const rgbMatch = color.match(
      /^rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)/i,
    );
    if (rgbMatch) {
      const [r, g, b] = rgbMatch.slice(1, 4).map(Number);
      return { r, g, b };
    }

    //Hex colors (#rgb, #rgba, #rrggbb and #rrggbbaa)
    const hexMatch = color.match(/^#([0-9a-f]{3,8})$/i);
    if (hexMatch && [3, 4, 6, 8].includes(hexMatch[1].length)) {
      let hex = hexMatch[1];
      if (hex.length <= 4) {
        hex = hex
          .split("")
          .map((char) => char + char)
          .join("");
      }
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }

    return null;
  }

  /**
   * This method will return the relative luminance (0 for black, 1 for white) of the given rgb values, as defined by WCAG
   * @param {{ r: number, g: number, b: number }} rgb
   * @returns {number}
   */
  static luminance({ r, g, b }) {
    const [red, green, blue] = [r, g, b].map((value) => {
      const channel = value / 255;
      return channel <= 0.03928
        ? channel / 12.92
        : Math.pow((channel + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  }

  /**
   * This method will return black or white, whichever is the most readable as text color on the given background color
   * @param {string} backgroundColor
   * @returns {string} "#000000" or "#ffffff"
   */
  static getReadableTextColor(backgroundColor) {
    const rgb = ColorHelper.toRgb(backgroundColor);
    if (!rgb) {
      return "#ffffff";
    }

    //Compare the WCAG contrast ratios of black and white text on the background
    const luminance = ColorHelper.luminance(rgb);
    const contrastWithWhite = 1.05 / (luminance + 0.05);
    const contrastWithBlack = (luminance + 0.05) / 0.05;

    return contrastWithBlack > contrastWithWhite ? "#000000" : "#ffffff";
  }
}

export default ColorHelper;
